import { useCallback, useMemo, useRef, useState } from 'react';
import { SearchProps } from './Search.types';
import { useControlledValue } from '../FormField/useControlledValue';
import { useDebounce } from '../../utils/useDebounce';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';

/**
 * Custom hook that manages the search component logic and state
 * @param props - The search component props
 * @returns Object containing component state and handlers
 */
const useSearchPresenter = (props: SearchProps) => {
  // Destructure props with default values
  const {
    style = 'Auto',
    onSearch,
    onClear,
    debounceMs = 300,
    minCharacters = 1,
    loading = false,
    value,
    initialValue,
    onValueChange,
    'component-variant': componentVariant,
    ...restProps
  } = props;

  // Handle controlled/uncontrolled value state
  const { value: controlledValue, onValueChange: handleValueChange } = useControlledValue({
    value,
    onValueChange,
    defaultValue: initialValue || '',
  });

  const isAutoStyle = useMemo(() => style === 'Auto', [style]);
  const isButtonStyle = useMemo(() => style === 'Button', [style]);

  // Track search loading state internally
  const [isSearching, setIsSearching] = useState(false);
  
  // Keep reference to last search query to prevent duplicate searches
  const lastSearchRef = useRef<string>('');

  /**
   * Executes the search callback and manages loading state
   * Prevents duplicate searches by comparing with last search query
   */
  const handleSearch = useCallback(async (query: string) => {
    // Skip if same query was already searched
    if (query === lastSearchRef.current) return;
    
    lastSearchRef.current = query;
    setIsSearching(true);
    
    try {
      await onSearch?.(query);
    } finally {
      setIsSearching(false);
    }
  }, [onSearch]);

  // Create debounced version of search for auto mode
  const { debouncedCallback: debouncedSearch, cancel: cancelDebounce } = useDebounce(
    handleSearch,
    debounceMs
  );

  /**
   * Handles input value changes and triggers search in auto mode
   * In auto mode: searches are debounced and only trigger when minimum characters are met
   * In manual mode: only updates the input value
   */
  const handleInputChange = useCallback((newValue: string) => {
    handleValueChange(newValue);

    // Only trigger search in auto mode
    if (isAutoStyle) {
      if (newValue.length >= minCharacters) {
        debouncedSearch(newValue);
      } else {
        // Cancel pending search if input is too short
        cancelDebounce();
      }
    }
  }, [isAutoStyle, minCharacters, debouncedSearch, cancelDebounce, handleValueChange]);

  /**
   * Handles manual search submission (for button clicks)
   * Only executes in manual mode when minimum characters requirement is met
   */
  const handleSubmit = useCallback(() => {
    if (isButtonStyle && controlledValue && controlledValue.length >= minCharacters) {
      handleSearch(controlledValue);
    }
  }, [isButtonStyle, controlledValue, minCharacters, handleSearch]);

  /**
   * Clears the search input and cancels any pending searches
   * Resets the last search reference to allow re-searching the same query
   * Calls the onClear callback if provided
   */
  const handleClear = useCallback(() => {
    handleValueChange('');
    lastSearchRef.current = '';
    cancelDebounce();
    onClear?.();
  }, [handleValueChange, cancelDebounce, onClear]);

  /**
   * Handles keyboard events on the input
   * In manual mode: triggers search when Enter key is pressed
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isButtonStyle) {
      e.preventDefault();
      if (controlledValue && controlledValue.length >= minCharacters) {
        handleSearch(controlledValue);
      }
    }
  }, [isButtonStyle, controlledValue, minCharacters, handleSearch]);


  const variantProps = useBuildVariantProps(
    'search',
    { 
      style,
      type: controlledValue?.length ? 'Filled' : 'Default',
    },
    {
      disabled: props.disabled,
      invalid: props.invalid,
    },
    componentVariant
  );

  // Return all the values and handlers needed by the view component
  return {
    // Current input value (controlled by useControlledValue)
    controlledValue,
    // Combined loading state from internal search and external loading prop
    isSearching: isSearching || loading,
    // Disable search when loading or when input is empty
    searchDisabled: isSearching || loading || controlledValue?.length === 0,
    // Event handlers
    handleInputChange,
    handleSubmit,
    handleClear,
    handleKeyDown,
    // UI state flags
    showClearButton: controlledValue?.length,
    isAutoStyle,
    isButtonStyle,
    variantProps,
    // Remaining props to pass to Input component
    restProps,
  };
};

export default useSearchPresenter;